import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import api from '../services/api';
import { Plus, Trash2, UserCheck, RefreshCw, Users, Settings, X, Pencil, UserX } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [showUserDrawer, setShowUserDrawer] = useState(false);
  const [userDrawerMode, setUserDrawerMode] = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSortConfig, setUserSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, categoriesRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/categories'),
      ]);
      setUsers(usersRes.data.data || []);
      setCategories(categoriesRes.data.data || []);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(`/admin/users/${id}/approve`);
      toast.success('User approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleNotApproved = async (id) => {
    try {
      await api.put(`/admin/users/${id}`, { status: 'pending' });
      toast.success('User moved to not approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleResetPassword = async (id) => {
    try {
      await api.post(`/admin/users/${id}/reset-password`);
      toast.success('Password reset sent to user');
      fetchData();
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const onSubmitUser = async (data) => {
    try {
      if (userDrawerMode === 'edit' && selectedUser?.id) {
        await api.put(`/admin/users/${selectedUser.id}`, data);
        toast.success('User updated successfully');
      } else {
        await api.post('/admin/users', data);
        toast.success('User created successfully');
      }
      setShowUserDrawer(false);
      setUserDrawerMode('add');
      setSelectedUser(null);
      reset();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || (userDrawerMode === 'edit' ? 'Failed to update user' : 'Failed to create user'));
    }
  };

  const openAddUserDrawer = () => {
    setUserDrawerMode('add');
    setSelectedUser(null);
    reset({
      username: '',
      email: '',
      full_name: '',
      mobile_number: '',
      role: 'member',
      status: 'approved',
    });
    setShowUserDrawer(true);
  };

  const openEditUserDrawer = (user) => {
    setUserDrawerMode('edit');
    setSelectedUser(user);
    reset({
      username: user?.username ?? '',
      email: user?.email ?? '',
      full_name: user?.full_name ?? '',
      mobile_number: user?.mobile_number ?? '',
      role: user?.role ?? 'member',
      status: user?.status ?? 'pending',
    });
    setShowUserDrawer(true);
  };

  const closeUserDrawer = () => {
    setShowUserDrawer(false);
    setUserDrawerMode('add');
    setSelectedUser(null);
    reset();
  };

  const requestUserSort = (key) => {
    setUserSortConfig((prev) => {
      const nextDirection = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction: nextDirection };
    });
  };

  const filteredUsers = useMemo(() => {
    const q = userSearchQuery.trim().toLowerCase();
    let rows = Array.isArray(users) ? [...users] : [];
    if (q) {
      rows = rows.filter((u) => {
        const haystack = [u?.username, u?.full_name, u?.email, u?.role, u?.status, u?.mobile_number]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    const { key, direction } = userSortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];

      if (key === 'created_at') {
        const ad = av ? new Date(av).getTime() : 0;
        const bd = bv ? new Date(bv).getTime() : 0;
        return (ad - bd) * dir;
      }

      const as = String(av ?? '').toLowerCase();
      const bs = String(bv ?? '').toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });
    return rows;
  }, [users, userSearchQuery, userSortConfig]);

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/admin/categories', Object.fromEntries(formData));
      toast.success('Category created');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create category');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="page-header">
        <h1 className="page-title">Admin Panel</h1>
        <p className="page-subtitle">Manage users, categories, and system settings</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'users'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Users
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'categories'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Settings className="w-4 h-4 inline mr-2" />
          Categories
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">User Management</h2>
            <button onClick={openAddUserDrawer} className="btn btn-primary">
              <Plus className="w-4 h-4" />
              Add User
            </button>
          </div>

          <div className="card">
            <div className="mb-4">
              <input
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="input"
                placeholder="Search users..."
              />
            </div>
            {filteredUsers.length > 0 ? (
              <div className="table-container overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="cursor-pointer select-none" onClick={() => requestUserSort('username')}>Username</th>
                      <th className="cursor-pointer select-none" onClick={() => requestUserSort('full_name')}>Name</th>
                      <th className="cursor-pointer select-none" onClick={() => requestUserSort('email')}>Email</th>
                      <th className="cursor-pointer select-none" onClick={() => requestUserSort('role')}>Role</th>
                      <th className="cursor-pointer select-none" onClick={() => requestUserSort('status')}>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td className="font-medium">{user.username}</td>
                        <td>{user.full_name}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className="badge badge-info capitalize">{user.role}</span>
                        </td>
                        <td>
                          <span className={`badge ${
                            user.status === 'approved' ? 'badge-success' :
                            user.status === 'pending' ? 'badge-warning' :
                            'badge-danger'
                          } capitalize`}>
                            {user.status}
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            {user.status === 'pending' && (
                              <button
                                onClick={() => handleApprove(user.id)}
                                className="p-1 text-green-600 hover:bg-green-100 rounded"
                                title="Approve"
                              >
                                <UserCheck className="w-4 h-4" />
                              </button>
                            )}
                            {user.status === 'approved' && (
                              <button
                                onClick={() => handleNotApproved(user.id)}
                                className="p-1 text-yellow-700 hover:bg-yellow-100 rounded"
                                title="Not Approved"
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => openEditUserDrawer(user)}
                              className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleResetPassword(user.id)}
                              className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                              title="Reset Password"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center py-8 text-gray-500">No users found</p>
            )}
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="space-y-6">
          <h2 className="text-lg font-semibold">Category Management</h2>

          {/* Add Category Form */}
          <div className="card">
            <h3 className="font-semibold mb-4">Add New Category</h3>
            <form onSubmit={handleCreateCategory} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Charity Type</label>
                <input type="text" name="charity_type" className="input" placeholder="e.g., Zakath" />
              </div>
              <div>
                <label className="label">Debit Type</label>
                <input type="text" name="debit_type" className="input" placeholder="e.g., Medical" />
              </div>
              <div>
                <label className="label">Cashier Name</label>
                <input type="text" name="cashier_list" className="input" placeholder="Cashier name" />
              </div>
              <div className="md:col-span-3">
                <button type="submit" className="btn btn-primary">
                  <Plus className="w-4 h-4" />
                  Add Category
                </button>
              </div>
            </form>
          </div>

          {/* Categories List */}
          <div className="card">
            {categories.length > 0 ? (
              <div className="table-container overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Charity Type</th>
                      <th>Debit Type</th>
                      <th>Cashier</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((cat) => (
                      <tr key={cat.id}>
                        <td>{cat.charity_type || '-'}</td>
                        <td>{cat.debit_type || '-'}</td>
                        <td>{cat.cashier_list || '-'}</td>
                        <td>
                          <button
                            onClick={() => handleDeleteCategory(cat.id)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center py-8 text-gray-500">No categories found</p>
            )}
          </div>
        </div>
      )}

      {/* User Drawer */}
      {showUserDrawer && (
        <>
          <div className="drawer-overlay" onClick={closeUserDrawer} />
          <div className="drawer-panel">
            <div className="h-full flex flex-col">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold">{userDrawerMode === 'edit' ? 'Edit User' : 'Add New User'}</h2>
                <button onClick={closeUserDrawer} className="p-2 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                <form onSubmit={handleSubmit(onSubmitUser)} className="space-y-4">
                  <div>
                    <label className="label">Username *</label>
                    <input
                      type="text"
                      {...register('username', { required: 'Username is required' })}
                      className={`input ${errors.username ? 'input-error' : ''}`}
                      disabled={userDrawerMode === 'edit'}
                    />
                  </div>
                  <div>
                    <label className="label">Email *</label>
                    <input
                      type="email"
                      {...register('email', { required: 'Email is required' })}
                      className={`input ${errors.email ? 'input-error' : ''}`}
                    />
                  </div>
                  <div>
                    <label className="label">Full Name *</label>
                    <input
                      type="text"
                      {...register('full_name', { required: 'Full name is required' })}
                      className={`input ${errors.full_name ? 'input-error' : ''}`}
                    />
                  </div>
                  <div>
                    <label className="label">Mobile Number</label>
                    <input type="tel" {...register('mobile_number')} className="input" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Role</label>
                      <select {...register('role')} className="input">
                        <option value="member">Member</option>
                        <option value="editor">Editor</option>
                        <option value="administrator">Administrator</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Status</label>
                      <select {...register('status')} className="input">
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <button type="button" onClick={closeUserDrawer} className="btn btn-outline">
                      Cancel
                    </button>
                    <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                      {isSubmitting ? 'Saving...' : (userDrawerMode === 'edit' ? 'Update User' : 'Create User')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
